import { PlusOutlined } from "@ant-design/icons";
import { Clear } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FieldArray, FormikProps, useField, useFormikContext } from "formik";
import { isNil, map, sumBy, toNumber } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { SelectOption } from "components/form/SelectOption";
import DeleteRecordDialog from "components/form/standard/DeleteRecordDialog";
import { TextFieldCurrency } from "components/form/TextFieldCurrency";
import { TextFieldPercent } from "components/form/TextFieldPercent";
import { useUsersDisplay } from "contexts/UserDisplayContext";
import { BROKER_ROLE_OPTIONS } from "pages/deal/constants/deal_enums";
import {
  formatCurrency,
  formatPercentageWithDecimal,
} from "pages/deal/utils/reporting";
import { BrokerRoleEnum } from "types/api/deal/commissions";

/**
 * Recalculate either `actual_percent` or `actual_amount` to keep them in-sync
 * with the deal-level `commission`.
 */
async function recalcRow(
  index: number,
  getFieldProps: FormikProps<any>["getFieldProps"],
  setFieldValue: FormikProps<any>["setFieldValue"],
  commission: number,
  recalcFields: ("actual_percent" | "actual_amount")[]
) {
  if (!commission) return;

  let percent =
    getFieldProps(`commission_split[${index}].actual_percent`).value ?? 0;
  let amount =
    getFieldProps(`commission_split[${index}].actual_amount`).value ?? 0;

  if (recalcFields.includes("actual_amount")) {
    amount = percent * commission;
    await setFieldValue(
      `commission_split[${index}].actual_amount`,
      Number.isFinite(amount) ? amount : null
    );
  }

  if (recalcFields.includes("actual_percent")) {
    percent = amount / commission;
    await setFieldValue(
      `commission_split[${index}].actual_percent`,
      Number.isFinite(percent) ? percent : null
    );
  }
}

/** ------------------------------------------------------------------------ */
/** Table cells (inline editable)                                            */
/** ------------------------------------------------------------------------ */

function AgentCell({
  index,
  userOptions,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  userOptions: { key: string | number; label: string }[];
  isEditing: (row: number, field: string) => boolean;
  startEditing: (row: number, field: string) => void;
  stopEditing: () => void;
}) {
  const [userField] = useField(`commission_split[${index}].user_id`);

  return (
    <TableCell
      onClick={() => startEditing(index, "user_id")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "user_id") ? (
        <SelectOption
          fieldName={`commission_split[${index}].user_id`}
          options={userOptions}
          showLabel={false}
          autoFocus
          onBlur={stopEditing}
        />
      ) : (
        (userOptions.find((o) => o.key === userField.value)?.label ?? "--")
      )}
    </TableCell>
  );
}

function BrokerRoleCell({
  index,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  isEditing: (row: number, field: string) => boolean;
  startEditing: (row: number, field: string) => void;
  stopEditing: () => void;
}) {
  const [brokerRoleField] = useField(`commission_split[${index}].broker_role`);

  return (
    <TableCell
      onClick={() => startEditing(index, "broker_role")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "broker_role") ? (
        <SelectOption
          fieldName={`commission_split[${index}].broker_role`}
          options={BROKER_ROLE_OPTIONS}
          showLabel={false}
          autoFocus
          onBlur={stopEditing}
        />
      ) : (
        (BROKER_ROLE_OPTIONS.find((o) => o.key === brokerRoleField.value)
          ?.label ?? "--")
      )}
    </TableCell>
  );
}

function PercentCell({ index, isEditing, startEditing, stopEditing }: any) {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const [percentField] = useField(`commission_split[${index}].actual_percent`);
  const [commissionField] = useField("commission");

  return (
    <TableCell
      onClick={() => startEditing(index, "actual_percent")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "actual_percent") ? (
        <TextFieldPercent
          fieldName={`commission_split.${index}.actual_percent`}
          showLabel={false}
          autoFocus
          onBlur={async () => {
            stopEditing();
            await recalcRow(
              index,
              getFieldProps,
              setFieldValue,
              commissionField.value,
              ["actual_amount"]
            );
          }}
        />
      ) : (
        formatPercentageWithDecimal(percentField.value)
      )}
    </TableCell>
  );
}

function AmountCell({ index, isEditing, startEditing, stopEditing }: any) {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const [amountField] = useField(`commission_split[${index}].actual_amount`);
  const [commissionField] = useField("commission");

  return (
    <TableCell
      onClick={() => startEditing(index, "actual_amount")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "actual_amount") ? (
        <TextFieldCurrency
          fieldName={`commission_split.${index}.actual_amount`}
          showLabel={false}
          autoFocus
          onBlur={async () => {
            stopEditing();
            await recalcRow(
              index,
              getFieldProps,
              setFieldValue,
              commissionField.value,
              ["actual_percent"]
            );
          }}
        />
      ) : (
        formatCurrency(amountField.value)
      )}
    </TableCell>
  );
}

/** ------------------------------------------------------------------------ */
/** Main field component                                                     */
/** ------------------------------------------------------------------------ */

export const CommissionShareField = () => {
  /* --------------------------------------------------------------------- */
  /* Formik context & local state                                          */
  /* --------------------------------------------------------------------- */
  const [commissionField] = useField<number>("commission");
  const [rowsField, , rowsHelpers] = useField("commission_split");

  const [rowToRemove, setRowToRemove] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState<{
    row: number | null;
    field: string | null;
  }>({
    row: null,
    field: null,
  });

  const isEditing = (row: number, field: string) =>
    editing.row === row && editing.field === field;
  const startEditing = (row: number, field: string) =>
    setEditing({ row, field });
  const stopEditing = () => setEditing({ row: null, field: null });

  /* --------------------------------------------------------------------- */
  /* Effects – keep amounts in-sync when deal commission changes           */
  /* --------------------------------------------------------------------- */
  const { getFieldProps, setFieldValue } = useFormikContext();

  useEffect(() => {
    const commission = commissionField.value;
    if (!commission) return;
    rowsField.value?.forEach(async (_: unknown, idx: number) => {
      await recalcRow(idx, getFieldProps, setFieldValue, commission, [
        "actual_amount",
      ]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commissionField.value]); // run only when commission changes

  /* --------------------------------------------------------------------- */
  /* User Options        */
  /* --------------------------------------------------------------------- */
  const usersDisplay = useUsersDisplay();
  const userOptions = useMemo(
    () =>
      map(usersDisplay, (value, key) => ({ key: toNumber(key), label: value })),
    [usersDisplay]
  );

  /* --------------------------------------------------------------------- */
  /* Row template                                                          */
  /* --------------------------------------------------------------------- */
  const blankRow = useCallback(
    () => ({
      user: null,
      broker_role: "none",
      estimate_percent: null,
      estimate_amount: null,
      actual_percent: null,
      actual_amount: null,
    }),
    []
  );

  /* --------------------------------------------------------------------- */
  /** Render                                                               */
  /* --------------------------------------------------------------------- */
  return (
    <FieldArray
      name="commission_split"
      render={({ remove, push }) => (
        <>
          <Stack spacing={1}>
            <InputLabel>Commission Split</InputLabel>

            <TableContainer>
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Broker</TableCell>
                    <TableCell>Commission&nbsp;%</TableCell>
                    <TableCell>Commission (Gross)</TableCell>
                    <TableCell>Broker Role</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rowsField.value?.map((_row: any, idx: number) => (
                    <TableRow
                      key={idx}
                      sx={{ "&:hover": { bgcolor: "transparent !important" } }}
                    >
                      <AgentCell
                        index={idx}
                        userOptions={userOptions}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <PercentCell
                        index={idx}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <AmountCell
                        index={idx}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <BrokerRoleCell
                        index={idx}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => setRowToRemove(idx)}
                        >
                          <Clear />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={1} align="right">
                      <strong>Totals:</strong>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Typography fontWeight={600}>
                        {formatPercentageWithDecimal(
                          sumBy(rowsField.value, "actual_percent") ?? 0
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        {formatCurrency(
                          sumBy(rowsField.value, "actual_amount") ?? 0
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <Box sx={{ pt: 2.5, pr: 2.5, pb: 2.5 }}>
              <Button
                startIcon={<PlusOutlined />}
                color="primary"
                variant="dashed"
                sx={{ bgcolor: "transparent !important" }}
                onClick={() => push(blankRow())}
              >
                Add Agent
              </Button>
            </Box>
          </Stack>

          {/* row-delete confirmation */}
          <DeleteRecordDialog
            open={!isNil(rowToRemove)}
            onClose={() => setRowToRemove(undefined)}
            onConfirm={() => {
              if (!isNil(rowToRemove)) remove(rowToRemove);
              setRowToRemove(undefined);
            }}
            displayName="row"
          />
        </>
      )}
    />
  );
};

export default CommissionShareField;
